import { type DecodedPeaks, loadPeaks } from '@/utils/audioPeaks';

export { type DecodedPeaks, loadPeaks };

export async function loadSampleWaveform(src: string, transientSrc?: string) {
  const [audio, reference] = await Promise.all([
    loadPeaks(src),
    loadPeaks(transientSrc ?? src),
  ]);
  const onset = findTransientRatio(reference.peaks[0] ?? []);
  return {
    ...audio,
    transientRatio:
      onset === undefined
        ? undefined
        : Math.min(1, (onset * reference.duration) / audio.duration),
  };
}

/**
 * How loud a bucket has to be, against the loudest one, to still count as
 * part of the attack. Walking back from the peak while buckets stay above
 * this finds where the click starts rather than where it is loudest, which
 * is the moment a listener hears it.
 */
const ONSET_FLOOR = 0.15;

/** Below this the sample is silence or noise, and naming a click in it would be invention. */
const MIN_TRANSIENT_PEAK = 0.02;

/**
 * ### findTransientRatio
 *
 * Where the sample's sharpest attack begins, as a share of its length. The
 * connector click is a single transient in an otherwise quiet recording, so
 * the loudest bucket locates it and walking back to the foot of that rise
 * gives its onset.
 *
 * The buckets are the same ones the waveform is drawn from, so the ratio is
 * both a position along the drawing and, multiplied by the duration, the
 * moment the playhead reaches it.
 *
 * @param peaks Absolute peak amplitude per bucket, in order
 * @returns The onset as a ratio in [0, 1), or undefined if nothing stands out
 */
export function findTransientRatio(
  peaks: ReadonlyArray<number>,
): number | undefined {
  if (peaks.length === 0) {
    return undefined;
  }

  let loudestIndex = 0;
  let loudest = 0;
  peaks.forEach((peak, index) => {
    if (peak > loudest) {
      loudest = peak;
      loudestIndex = index;
    }
  });

  if (loudest < MIN_TRANSIENT_PEAK) {
    return undefined;
  }

  let onset = loudestIndex;
  while (onset > 0 && (peaks[onset - 1] ?? 0) > loudest * ONSET_FLOOR) {
    onset -= 1;
  }

  return onset / peaks.length;
}
